import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faExternalLinkAlt, faChevronRight, faChevronDown, faFilePdf, faUpload, faCamera, faInfoCircle, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { DeleteQuoteForm, SendQuoteForm, AcceptQuoteForm, EditAddressForm, UpdateQuoteForm, ManageIncludedDesignsForm } from '../forms';
import { useAppContext } from '../../context/AppContext';
import { NewQuoteForm } from '../forms';
import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { findSkyCivPdfForDesign, openUploadInNewTab } from '../../utils/openUploadFile';
import { formatBillingAmount, formatBillingDate, formatBillingDateTime } from '../../utils/billingTables';
import { LeadTimeNotice } from '../helpers/LeadTimeNotice';
import { ActionsDropdown, ActionsDropdownItem, ActionsDropdownDivider } from './ActionsDropdown';
import { TableEmptyState } from './TableEmptyState';
import { isEmptyAddress } from '../../utils/projects';

const getIncludedDesignIds = (quote) => {
    const raw = quote.included_designs ?? quote.includedDesigns;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [];
};

export const QuotesTable = ({
    project,
    designs = [],
    files = [],
    quotes,
    setQuotes,
    onUploadTraveler,
    onUploadPhotosForDesign,
    newQuotePrefill,
    onNewQuotePrefillConsumed,
    /** 'full' (default) | 'header' (Get Quote + warnings only) | 'table' (quotes list only) */
    section = 'full',
    showProjectColumn = false,
    highlightPending = false,
}) => {
    const { token, base_url, user } = useAppContext();
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [manageDesignsModal, setManageDesignsModal] = useState(null);
    const [expandedQuoteIds, setExpandedQuoteIds] = useState({});

    const hasBillingEmail = Boolean(project?.billing_address?.email?.trim());

    const setDropdownOpen = useCallback((quoteId, open) => {
        setOpenDropdownId(open ? quoteId : null);
    }, []);

    const toggleQuoteDesignsExpanded = (quoteId) => {
        setExpandedQuoteIds((prev) => ({ ...prev, [quoteId]: !prev[quoteId] }));
    };

    const colCount = showProjectColumn ? 7 : 6;

    const renderQuoteSection = () => {
        if (!project) return null;

        if (project.company_id === 1) {
            return (
                <div className="quotes-table-link">
                    <p>Project ownership must be set to a company other than MT Solar to create a quote.</p>
                </div>
            );
        }
        
        if (isEmptyAddress(project.shipping_address)) {
            return (
                <div className="banner banner--warning banner--lg">
                    <p className="banner__title">Shipping Address Required</p>
                    <p className="banner__text">Add a shipping address for this project to create a quote.</p>
                    <EditAddressForm project={project} addressType="shipping" buttonLabel="Add Shipping Address" />
                </div>
            );
        }

        if (!hasBillingEmail) {
            return (
                <div className="banner banner--warning banner--lg">
                    <p className="banner__title">Billing Email Required</p>
                    <p className="banner__text">Add an email to the billing address for this project to create a quote.</p>
                    <EditAddressForm project={project} addressType="billing" buttonLabel="Add Billing Email" />
                </div>
            );
        }
        
        return (
            <>
            <LeadTimeNotice designs={designs} />
            <div className="quotes-header-toolbar">
            <NewQuoteForm
                project={project}
                designs={designs}
                setQuotes={setQuotes}
                newQuotePrefill={newQuotePrefill}
                onNewQuotePrefillConsumed={onNewQuotePrefillConsumed}
            />
            <div className="quotes-tab-info" role="status">
                <FontAwesomeIcon icon={faInfoCircle} className="quotes-tab-info-icon" aria-hidden />
                <span>Quotes automatically expire after 30 days.</span>
            </div>
            </div>
            </>
        );
    };

    const handleViewSkycivPdf = async (design) => {
        if (!design?.id) {
            window.showAlert(
                404,
                'No documents were found for this design. Please review the Files tab.'
            );
            return;
        }
        const upload = findSkyCivPdfForDesign(files, design.id);
        if (!upload) {
            window.showAlert(
                404,
                'No documents were found for this design. Please review the Files tab.'
            );
            return;
        }
        await openUploadInNewTab(upload, token, base_url);
    };

    const handleUploadTravelerClick = (design) => {
        if (!onUploadTraveler) {
            window.showAlert(
                400,
                'Open the project page to upload a traveler for this design.'
            );
            return;
        }
        if (!design?.id) {
            window.showAlert(
                404,
                'No design record is available for this row. Please review the Files tab.'
            );
            return;
        }
        onUploadTraveler(design);
    };

    const handleUploadPhotosClick = (design) => {
        if (!onUploadPhotosForDesign) {
            window.showAlert(
                400,
                'Open the project page to upload photos for this design.'
            );
            return;
        }
        if (!design?.id) {
            window.showAlert(
                404,
                'No design record is available for this row. Please review the Files tab.'
            );
            return;
        }
        onUploadPhotosForDesign(design);
    };

    const downloadPDF = async (quote) => {
        if(!token) {
            window.showAlert(401, "Please login to download PDF");
        }
      
        const res = await fetch(`${base_url}/apis/billing/${quote.id}/pdf`, {
          headers: {
            Authorization: token,
          },
        });
      
        if (!res.ok) {
          alert("Unauthorized or error fetching PDF");
          return;
        }
      
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
    };
      

    const quoteHeader = renderQuoteSection();

    const quoteTable = (
            <div className="table-responsive">
                {quotes.length === 0 ? (
                    <TableEmptyState>
                        <p>
                            {showProjectColumn
                                ? 'No quotes available.'
                                : 'No quotes available for this project.'}
                        </p>
                        {!showProjectColumn && (
                            <p>Click the &quot;Get a Quote&quot; button above to create your first quote.</p>
                        )}
                    </TableEmptyState>
                ) : (
                    <table className="data-table quotes-table billing-document-table">
                        <colgroup>
                            {showProjectColumn ? <col className="col-lg hide-on-mobile" /> : null}
                            <col className="col-quote-number" />
                            <col className="col-date hide-on-mobile" />
                            <col className="col-date hide-on-mobile" />
                            <col className="col-status hide-on-mobile" />
                            <col className="col-amount" />
                            <col className="col-actions" />
                        </colgroup>
                        <thead>
                            <tr>
                                {showProjectColumn && <th className="col-lg hide-on-mobile">Project</th>}
                                <th className="col-quote-number">Quote Number</th>
                                <th className="col-date hide-on-mobile">Created</th>
                                <th className="col-date hide-on-mobile">Last Sent</th>
                                <th className="col-status hide-on-mobile">Status</th>
                                <th className="col-amount">Total</th>
                                <th className="col-actions billing-col-actions-header">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...quotes]
                                .sort((a, b) => {
                                    const dateA = new Date(a.created_at || 0).getTime();
                                    const dateB = new Date(b.created_at || 0).getTime();
                                    return dateB - dateA;
                                })
                                .map((quote) => {
                                    const includedIds = getIncludedDesignIds(quote);
                                    const designRows = includedIds.map((uid) => ({
                                        uid,
                                        design: designs.find((d) => d.unique_id === uid),
                                    }));
                                    const isDesignsExpanded = Boolean(expandedQuoteIds[quote.id]);
                                    const isHighlighted = highlightPending && user?.is_super_admin && !quote.is_accepted;

                                    return (
                                        <React.Fragment key={quote.id}>
                                            <tr className={`quote-main-row${isHighlighted ? ' table-row--highlighted' : ''}`}>
                                                {showProjectColumn && (
                                                    <td className="col-lg hide-on-mobile">
                                                        {quote.project_site_id ? (
                                                            <Link to={`/project/${quote.project_site_id}?tab=quotes`}>
                                                                {quote.project_site_name || `Project ${quote.project_site_id}`}
                                                            </Link>
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </td>
                                                )}
                                                <td className="col-quote-number billing-doc-number-cell">
                                                    <div className="billing-doc-number-row">
                                                        <span className="billing-doc-number-text">{quote.quote_number}</span>
                                                        {includedIds.length > 0 && (
                                                            <button
                                                                type="button"
                                                                className="quote-expand-btn"
                                                                onClick={() => toggleQuoteDesignsExpanded(quote.id)}
                                                                aria-expanded={isDesignsExpanded}
                                                                aria-label={isDesignsExpanded ? 'Hide designs on quote' : 'Show designs on quote'}
                                                                title={isDesignsExpanded ? 'Hide designs on quote' : 'Show designs on quote'}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={isDesignsExpanded ? faChevronDown : faChevronRight}
                                                                />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="col-date hide-on-mobile">{formatBillingDate(quote.created_at)}</td>
                                                <td className="col-date hide-on-mobile">
                                                    {formatBillingDateTime(quote.last_sent_from_portal)}
                                                </td>
                                                <td className="col-status hide-on-mobile">{quote.is_accepted ? 'Accepted' : 'Pending'}</td>
                                                <td className="col-amount">{formatBillingAmount(quote.total_amount)}</td>
                                                <td className="col-actions billing-col-actions-cell">
                                                    <ActionsDropdown
                                                        isOpen={openDropdownId === quote.id}
                                                        onOpenChange={(open) => setDropdownOpen(quote.id, open)}
                                                        estimatedMenuHeight={300}
                                                    >
                                                        {!quote.is_accepted && (user.company_id !== 1 || [8, 4, 100, 34, 1, 6, 67, 7].includes(user.id)) && (
                                                            <ActionsDropdownItem>
                                                                <AcceptQuoteForm
                                                                    quote={quote}
                                                                    setQuotes={setQuotes}
                                                                />
                                                            </ActionsDropdownItem>
                                                        )}
                                                        <ActionsDropdownItem>
                                                            <button onClick={() => { downloadPDF(quote); setOpenDropdownId(null); }} className="btn btn-secondary btn-action">
                                                                <FontAwesomeIcon icon={faFile} /> Download PDF
                                                            </button>
                                                        </ActionsDropdownItem>
                                                        <ActionsDropdownItem>
                                                            <SendQuoteForm quote={quote} setQuotes={setQuotes} />
                                                        </ActionsDropdownItem>
                                                        {user.is_super_admin && (
                                                            <>
                                                                <ActionsDropdownDivider />
                                                                <ActionsDropdownItem>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-action btn-secondary"
                                                                        title="Manage included designs"
                                                                        onClick={() => {
                                                                            setManageDesignsModal({ type: 'quote', record: quote });
                                                                            setOpenDropdownId(null);
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faLayerGroup} /> Manage Designs
                                                                    </button>
                                                                </ActionsDropdownItem>
                                                                {quote.qbo_data && (
                                                                    <ActionsDropdownItem>
                                                                        <a
                                                                            href={`https://qbo.intuit.com/app/estimate?txnId=${quote.qbo_data['Id']}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="btn btn-action btn-secondary"
                                                                            title="Open in QuickBooks"
                                                                        >
                                                                            <FontAwesomeIcon icon={faExternalLinkAlt} /> Open in QuickBooks
                                                                        </a>
                                                                    </ActionsDropdownItem>
                                                                )}
                                                                <ActionsDropdownItem>
                                                                    <UpdateQuoteForm quote={quote} setQuotes={setQuotes} />
                                                                </ActionsDropdownItem>
                                                            </>
                                                        )}
                                                        {(user.is_super_admin || !quote.is_accepted) && (
                                                            <>
                                                                <ActionsDropdownDivider />
                                                                <ActionsDropdownItem>
                                                                    <DeleteQuoteForm quote={quote} setQuotes={setQuotes} />
                                                                </ActionsDropdownItem>
                                                            </>
                                                        )}
                                                    </ActionsDropdown>
                                                </td>
                                            </tr>
                                            {includedIds.length > 0 &&
                                                isDesignsExpanded &&
                                                designRows.map(({ uid, design }) => (
                                                    <tr key={`${quote.id}-design-${uid}`} className="quote-design-subrow">
                                                        <td colSpan={colCount}>
                                                            <div className="quote-design-subrow-inner">
                                                                <div className="quote-design-subrow-main">
                                                                    {design ? (
                                                                        <>
                                                                            <Link
                                                                                className="quote-design-subrow-link"
                                                                                to={`/view-design/${design.id}`}
                                                                            >
                                                                                {design.design_name}
                                                                            </Link>
                                                                            <span className="quote-design-subrow-meta">
                                                                                <span className="quote-design-subrow-label">SKU</span>
                                                                                {design.unique_id}
                                                                            </span>
                                                                        </>
                                                                    ) : (
                                                                        <span className="quote-design-subrow-missing">
                                                                            <span className="quote-design-subrow-label">SKU</span>
                                                                            {uid}
                                                                            <span className="quote-design-subrow-hint">
                                                                                {' '}
                                                                                (not found on this project)
                                                                            </span>
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="quote-design-subrow-actions">
                                                                    {user?.is_super_admin && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-action btn-secondary design-subrow-action-btn"
                                                                            onClick={() => handleUploadPhotosClick(design)}
                                                                        >
                                                                            <FontAwesomeIcon icon={faCamera} />
                                                                            Upload Photos
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-action btn-secondary design-subrow-action-btn"
                                                                        onClick={() => handleViewSkycivPdf(design)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faFilePdf} />
                                                                        View Skyciv PDF
                                                                    </button>
                                                                    {user?.is_super_admin && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-action btn-secondary design-subrow-action-btn"
                                                                            onClick={() => handleUploadTravelerClick(design)}
                                                                        >
                                                                            <FontAwesomeIcon icon={faUpload} />
                                                                            Upload Traveler
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </React.Fragment>
                                    );
                                })}
                        </tbody>
                    </table>
                )}
            </div>
    );

    const manageDesignsModalElement = manageDesignsModal ? (
        <ManageIncludedDesignsForm
            type={manageDesignsModal.type}
            record={manageDesignsModal.record}
            designs={designs}
            setQuotes={setQuotes}
            isOpen={true}
            onClose={() => setManageDesignsModal(null)}
        />
    ) : null;

    if (section === 'header') {
        return <div className="billing-quotes-header">{quoteHeader}</div>;
    }

    if (section === 'table') {
        return (
            <div>
                {quoteTable}
                {manageDesignsModalElement}
            </div>
        );
    }

    return (
        <div>
            {quoteHeader && (
                <>
                    {quoteHeader}
                    <br />
                </>
            )}
            {quoteTable}
            {manageDesignsModalElement}
        </div>
    );
}; 